import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import { notificationsApi } from "../../services/api/notifications-advanced.api";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`notifications-tabpanel-${index}`}
      aria-labelledby={`notifications-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const NotificationsApiTest: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<Record<string, any>>({});
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [firstNotificationId, setFirstNotificationId] = useState<string | null>(
    null,
  );
  const [firstTemplateId, setFirstTemplateId] = useState<string | null>(null);

  const testEndpoint = async (name: string, testFn: () => Promise<any>) => {
    setLoading(true);
    setError(null);
    try {
      const result = await testFn();
      setResults((prev) => ({
        ...prev,
        [name]: { success: true, data: result },
      }));
    } catch (err: any) {
      setResults((prev) => ({
        ...prev,
        [name]: {
          success: false,
          error: err.message || "Неизвестная ошибка",
        },
      }));
      setError(`${name}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Управление уведомлениями
  const testGetNotifications = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await notificationsApi.getNotifications();
      setResults((prev) => ({
        ...prev,
        "Get Notifications": { success: true, data: result },
      }));

      // Сохраняем ID первого уведомления для тестирования других endpoints
      let notificationsArray: any[] | null = null;
      if (result && typeof result === "object" && "data" in result) {
        if (Array.isArray(result.data)) {
          notificationsArray = result.data;
        } else if (
          result.data &&
          typeof result.data === "object" &&
          "notifications" in result.data
        ) {
          notificationsArray = (result.data as any).notifications;
        }
      }

      if (notificationsArray && notificationsArray.length > 0) {
        setFirstNotificationId(notificationsArray[0].id);
      } else {
        setFirstNotificationId(null);
      }
    } catch (err: any) {
      setResults((prev) => ({
        ...prev,
        "Get Notifications": {
          success: false,
          error: err.message || "Неизвестная ошибка",
        },
      }));
      setError(`Get Notifications: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  const testGetNotification = async () => {
    if (!firstNotificationId) {
      setError("");
      setResults((prev) => ({
        ...prev,
        "Get Notification": {
          success: false,
          error:
            "Нет доступных уведомлений для тестирования. Сначала выполните тест Get Notifications.",
        },
      }));
      return;
    }
    await testEndpoint("Get Notification", () =>
      notificationsApi.getNotification(firstNotificationId),
    );
  };

  const testMarkAsRead = async () => {
    if (!firstNotificationId) {
      setError("");
      setResults((prev) => ({
        ...prev,
        "Mark As Read": {
          success: false,
          error:
            "Нет доступных уведомлений для тестирования. Сначала выполните тест Get Notifications.",
        },
      }));
      return;
    }
    await testEndpoint("Mark As Read", () =>
      notificationsApi.markAsRead(firstNotificationId),
    );
  };

  const testMarkAsUnread = async () => {
    if (!firstNotificationId) {
      setError("");
      setResults((prev) => ({
        ...prev,
        "Mark As Unread": {
          success: false,
          error:
            "Нет доступных уведомлений для тестирования. Сначала выполните тест Get Notifications.",
        },
      }));
      return;
    }
    await testEndpoint("Mark As Unread", () =>
      notificationsApi.markAsUnread(firstNotificationId),
    );
  };

  const testDeleteNotification = async () => {
    if (!firstNotificationId) {
      setError("");
      setResults((prev) => ({
        ...prev,
        "Delete Notification": {
          success: false,
          error:
            "Нет доступных уведомлений для тестирования. Сначала выполните тест Get Notifications.",
        },
      }));
      return;
    }
    await testEndpoint("Delete Notification", () =>
      notificationsApi.deleteNotification(firstNotificationId),
    );
  };
  const testClearAllNotifications = () =>
    testEndpoint("Clear All Notifications", () =>
      notificationsApi.clearAllNotifications(),
    );

  // Настройки уведомлений
  const testGetNotificationSettings = () =>
    testEndpoint("Get Notification Settings", () =>
      notificationsApi.getNotificationSettings(),
    );
  const testUpdateNotificationSettings = () =>
    testEndpoint("Update Notification Settings", () =>
      notificationsApi.updateNotificationSettings({
        email: {
          enabled: true,
          types: ["info"],
          frequency: "immediate",
        },
      }),
    );
  // W1-S5 (NTF-001): тест Send Test Notification удалён — V2-роут POST /notifications/test удалён на BE

  // Шаблоны уведомлений
  const testGetNotificationTemplates = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await notificationsApi.getNotificationTemplates();
      setResults((prev) => ({
        ...prev,
        "Get Notification Templates": { success: true, data: result },
      }));

      // Сохраняем ID первого шаблона для тестирования Get Notification Template
      let templatesArray: any[] | null = null;
      if (result && typeof result === "object" && "data" in result) {
        if (Array.isArray(result.data)) {
          templatesArray = result.data;
        } else if (
          result.data &&
          typeof result.data === "object" &&
          "templates" in result.data
        ) {
          templatesArray = (result.data as any).templates;
        }
      }

      if (templatesArray && templatesArray.length > 0) {
        setFirstTemplateId(templatesArray[0].id);
      } else {
        setFirstTemplateId(null);
      }
    } catch (err: any) {
      setResults((prev) => ({
        ...prev,
        "Get Notification Templates": {
          success: false,
          error: err.message || "Неизвестная ошибка",
        },
      }));
      setError(`Get Notification Templates: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  const testGetNotificationTemplate = async () => {
    if (!firstTemplateId) {
      setError("");
      setResults((prev) => ({
        ...prev,
        "Get Notification Template": {
          success: false,
          error:
            "Нет доступных шаблонов для тестирования. Сначала выполните тест Get Notification Templates.",
        },
      }));
      return;
    }
    await testEndpoint("Get Notification Template", () =>
      notificationsApi.getNotificationTemplate(firstTemplateId),
    );
  };
  const testCreateNotificationTemplate = () =>
    testEndpoint("Create Notification Template", () =>
      notificationsApi.createNotificationTemplate({
        name: "Test Template",
        type: "info",
        subject: "Test Subject",
        message: "Test Message",
        variables: ["userName", "eventName"],
      }),
    );
  const testUpdateNotificationTemplate = () =>
    testEndpoint("Update Notification Template", () =>
      notificationsApi.updateNotificationTemplate("test-template-id", {
        name: "Updated Template",
        isActive: true,
      }),
    );
  const testDeleteNotificationTemplate = () =>
    testEndpoint("Delete Notification Template", () =>
      notificationsApi.deleteNotificationTemplate("test-template-id"),
    );

  const testAllManagementEndpoints = async () => {
    await testGetNotifications();
    await testGetNotification();
    await testMarkAsRead();
    await testMarkAsUnread();
  };

  const testAllSettingsEndpoints = async () => {
    await testGetNotificationSettings();
    await testUpdateNotificationSettings();
  };

  const testAllTemplatesEndpoints = async () => {
    await testGetNotificationTemplates();
    await testGetNotificationTemplate();
    await testCreateNotificationTemplate();
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const clearResults = () => {
    setResults({});
    setError(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        🔔 Тестирование Notifications API
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Тестирование 15 endpoints из Notifications модуля
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="notifications test tabs"
        >
          <Tab label="Управление уведомлениями" />
          <Tab label="Настройки уведомлений" />
          <Tab label="Шаблоны уведомлений" />
          <Tab label="Все результаты" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button
              variant="contained"
              onClick={testAllManagementEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все endpoints управления
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testGetNotifications}
              disabled={loading}
            >
              Тест Get Notifications
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testGetNotification}
              disabled={loading}
            >
              Тест Get Notification
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testMarkAsRead}
              disabled={loading}
            >
              Тест Mark As Read
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testMarkAsUnread}
              disabled={loading}
            >
              Тест Mark As Unread
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testDeleteNotification}
              disabled={loading}
            >
              Тест Delete Notification
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testClearAllNotifications}
              disabled={loading}
            >
              Тест Clear All
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button
              variant="contained"
              onClick={testAllSettingsEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все endpoints настроек
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testGetNotificationSettings}
              disabled={loading}
            >
              Тест Get Settings
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testUpdateNotificationSettings}
              disabled={loading}
            >
              Тест Update Settings
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item>
            <Button
              variant="contained"
              onClick={testAllTemplatesEndpoints}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              Тестировать все endpoints шаблонов
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testGetNotificationTemplates}
              disabled={loading}
            >
              Тест Get Templates
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testGetNotificationTemplate}
              disabled={loading}
            >
              Тест Get Template
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testCreateNotificationTemplate}
              disabled={loading}
            >
              Тест Create Template
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testUpdateNotificationTemplate}
              disabled={loading}
            >
              Тест Update Template
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={testDeleteNotificationTemplate}
              disabled={loading}
            >
              Тест Delete Template
            </Button>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ mb: 3 }}>
          <Button variant="outlined" onClick={clearResults} disabled={loading}>
            Очистить результаты
          </Button>
        </Box>

        <Grid container spacing={2}>
          {Object.entries(results).map(([name, result]) => (
            <Grid item xs={12} md={6} key={name}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {name}
                  </Typography>
                  {result.success ? (
                    <Alert severity="success">
                      ✅ Успешно
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {JSON.stringify(result.data, null, 2)}
                      </Typography>
                    </Alert>
                  ) : (
                    <Alert severity="error">❌ Ошибка: {result.error}</Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {Object.keys(results).length === 0 && (
          <Card>
            <CardContent>
              <Typography variant="body1" color="text.secondary" align="center">
                Нажмите кнопку для начала тестирования
              </Typography>
            </CardContent>
          </Card>
        )}
      </TabPanel>
    </Box>
  );
};

export default NotificationsApiTest;
